import React, { useEffect, useState } from "react";
import { ThemeProvider, useTheme } from "next-themes";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import Navbar from "@/components/brams/Navbar";
import Hero from "@/components/brams/Hero";
import Services from "@/components/brams/Services";
import Realisations from "@/components/brams/Realisations";
import Portfolio from "@/components/brams/Portfolio";
import About from "@/components/brams/About";
import Testimonials from "@/components/brams/Testimonials";
import Contact from "@/components/brams/Contact";
import MobileApp from "@/components/brams/MobileApp";
import Footer from "@/components/brams/Footer";
import Grain from "@/components/brams/Grain";
import SiteBackground from "@/components/brams/SiteBackground";
import Intro from "@/components/brams/Intro";
import LogosBranding from "@/pages/LogosBranding";
import FlyersPrint from "@/pages/FlyersPrint";
import WebDesign from "@/pages/WebDesign";
import Impression from "@/pages/Impression";
import SocialMedia from "@/pages/SocialMedia";
import BrandingDirection from "@/pages/BrandingDirection";

gsap.registerPlugin(ScrollToPlugin);

function BodyThemeSync() {
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("dark", resolvedTheme === "dark");
  }, [resolvedTheme]);
  return null;
}

function HomePage() {
  const location = useLocation();
  const scrollTarget = location.state?.scrollTo || null;
  // Skip intro when arriving from a service page exit
  const [introOpen, setIntroOpen] = useState(() => !scrollTarget);

  // Handle deep-scroll request from service page exits
  useEffect(() => {
    if (!scrollTarget) return;

    // Ensure body scroll is enabled (in case it was locked by Intro)
    document.body.style.overflow = "";

    let cancelled = false;

    const performScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(scrollTarget);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;

      let offset;
      if (scrollTarget === "contact") {
        offset = Math.max(0, absoluteTop - 20);
      } else {
        offset = Math.max(
          0,
          absoluteTop - Math.max(0, (window.innerHeight - rect.height) / 2)
        );
      }

      gsap.to(window, {
        duration: 1.2,
        scrollTo: { y: offset, autoKill: false },
        ease: "power3.inOut",
      });

      if (scrollTarget === "contact") {
        const form = document.querySelector('[data-testid="contact-form"]');
        if (form) {
          gsap.fromTo(
            form,
            { opacity: 0.4, y: 16 },
            { opacity: 1, y: 0, duration: 0.8, delay: 0.6, ease: "power3.out" }
          );
        }
      }

      window.history.replaceState({}, document.title);
    };

    // Wait for all images currently in DOM to load, so layout is stable.
    const waitImages = () => {
      const imgs = Array.from(document.images).filter((i) => !i.complete);
      if (imgs.length === 0) return Promise.resolve();
      return Promise.all(
        imgs.map(
          (img) =>
            new Promise((resolve) => {
              const done = () => resolve();
              img.addEventListener("load", done, { once: true });
              img.addEventListener("error", done, { once: true });
            })
        )
      );
    };

    // Hard cap: 1500ms — start scroll even if some images are still loading.
    const cap = new Promise((resolve) => setTimeout(resolve, 1500));

    Promise.race([waitImages(), cap]).then(() => {
      // Two rAFs to ensure layout is settled
      requestAnimationFrame(() =>
        requestAnimationFrame(() => performScroll())
      );
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="App relative" data-testid="home-page">
      <SiteBackground />
      <Grain />
      {introOpen && <Intro onEnter={() => setIntroOpen(false)} />}
      <Navbar />
      <main className="relative z-[1]">
        <Hero />
        <Services />
        <Realisations />
        <Portfolio />
        <About />
        <Testimonials />
        <MobileApp />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <BodyThemeSync />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services/logos-branding" element={<LogosBranding />} />
          <Route path="/services/flyers-print" element={<FlyersPrint />} />
          <Route path="/services/web-design" element={<WebDesign />} />
          <Route path="/services/impression" element={<Impression />} />
          <Route path="/services/social-media" element={<SocialMedia />} />
          <Route path="/services/branding-direction" element={<BrandingDirection />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </ThemeProvider>
  );
}
